/* eslint-disable array-bracket-newline */
import { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Chart, registerables } from 'chart.js';
import { ILiquidityGraphEntity } from '@kattana-io/common';
import { roundToPrecision } from '../../helpers/truncate';
import Text from '../../atoms/Text';
import Loader from '../common/Loader';
import { KattanaAPI } from '../../store/createStore';
import { authTokenSelector } from '../../selectors/auth';

Chart.register(...registerables);

type NormalizedData = { price: number; liquidity: number }[];

interface ILiquidityChartProps {
  chain: string;
  address: string;
}

const normalizeData = (data: ILiquidityGraphEntity[]): NormalizedData => {
  return data
    .map((item) => ({
      liquidity: Math.abs(parseFloat(item.liquidity)),
      price: parseFloat(item.price),
    }))
    .filter((item) => item.liquidity > 0 && item.price > 0);
};

const LiquidityChart = ({ chain, address }: ILiquidityChartProps) => {
  const [data, setData] = useState<NormalizedData>([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef<{
    gradients: { green?: CanvasGradient; red?: CanvasGradient };
    chart?: Chart;
  }>({ gradients: {} });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const authToken = useSelector(authTokenSelector);

  useEffect(() => {
    const ref = chartRef.current;

    if (ref.chart && data.length) {
      const green = ref.gradients.green;
      const red = ref.gradients.red;
      const borderColors = data.map(() => (Math.random() > 0.5 ? green : red));
      const prices = data.map((item) => item.price);
      ref.chart.data.labels = prices;
      const dataset = ref.chart.data.datasets[0];
      const liquidities = data.map((item) => item.liquidity);
      dataset.data = liquidities;
      dataset.borderColor = borderColors;
      ref.chart.options.plugins!.tooltip!.callbacks = {
        title: function (tooltipItem) {
          return (
            'Liquidity: ' +
            roundToPrecision(
              Number(liquidities[tooltipItem[0].dataIndex]),
              true
            )
          );
        },
        label: function (tooltipItem) {
          return (
            'Price: ' +
            roundToPrecision(Number(prices[tooltipItem.dataIndex]), true, 6)
          );
        },
      };
      const pricesCallback = (_: number | string, i: number) => {
        return roundToPrecision(prices[i], true);
      };
      const liquiditiesCallback = (value: number | string) => {
        const strLength = value.toString().split('.')[0].length;

        return roundToPrecision(
          Number(value),
          true,
          strLength < 4 ? 4 - strLength : 0
        );
      };
      ref.chart.options.scales!.y!.ticks!.callback = pricesCallback;
      ref.chart.options.scales!.x!.ticks!.callback = liquiditiesCallback;

      ref.chart.update();
    }
  }, [data]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    KattanaAPI.liquidityGraph(chain, address, authToken, controller).then(
      (data) => {
        if (data?.length) {
          setData(normalizeData(data));
        } else {
          setData([]);
        }
        setLoading(false);
      }
    );

    return () => controller.abort();
  }, [address, authToken, chain]);

  useEffect(() => {
    const ref = chartRef.current;
    window.requestIdleCallback(() => {
      if (canvasRef.current) {
        // Chart
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          const gradientGreen = ctx.createLinearGradient(0, 0, 150, 0);
          gradientGreen.addColorStop(0, 'rgba(40, 238, 180, 0.8)'); // Green at the start
          gradientGreen.addColorStop(0, 'rgba(40, 238, 180, 0.7)'); // Green at the end

          const gradientRed = ctx.createLinearGradient(0, 0, 150, 0);
          gradientRed.addColorStop(0, 'rgba(245, 11, 0, 0.8)'); // Red at the start
          gradientRed.addColorStop(1, 'rgba(245, 11, 0, 0.7)'); // Red at the end

          ref.gradients.green = gradientGreen;
          ref.gradients.red = gradientRed;
          ref.chart = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: [],
              datasets: [
                {
                  data: [],
                  borderColor: 'rgba(108, 117, 125, 0.2)', // Light grey border color
                  borderWidth: 0.2,
                  barThickness: 'flex',
                  borderRadius: 2,
                },
              ],
            },
            options: {
              indexAxis: 'y',
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  titleColor: 'white',
                  bodyColor: 'white',
                  borderWidth: 1,
                  borderColor: 'rgba(108, 117, 125, 0.5)', // Light grey border color
                  bodySpacing: 5,
                  titleSpacing: 5,
                },
              },
              scales: {
                x: {
                  beginAtZero: false,
                  reverse: true,
                  title: {
                    display: true,
                    text: 'Liquidity',
                    color: 'rgb(173, 181, 199)',
                    font: {
                      size: 10,
                      weight: 'bold',
                    },
                  },
                  grid: {
                    color: 'rgba(108, 117, 125, 0.5)', // Light grey grid color
                    lineWidth: 0.5,
                  },
                  ticks: {
                    color: 'rgb(173, 181, 189)', // Light grey tick color
                  },
                },
                y: {
                  beginAtZero: false,
                  position: 'right',
                  title: {
                    display: true,
                    text: 'Price',
                    color: 'rgb(173, 181, 199)',
                    font: {
                      size: 10,
                      weight: 'bold',
                    },
                  },
                  grid: {
                    color: 'rgba(108, 117, 125, 0.5)', // Light grey grid color
                    lineWidth: 0.5,
                  },
                  ticks: {
                    color: 'rgb(173, 181, 189)', // Light grey tick color
                  },
                },
              },
            },
          });
        }
      }
    });

    return () => {
      ref.chart?.destroy();
    };
  }, []);

  const containerStyles = {
    width: 190,
    minWidth: 190,
    maxWidth: 190,
    height: '100%',
    maxHeight: '100%',
    minHeight: '100%',
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          ...containerStyles,
          display: !!data.length && !loading ? undefined : 'none',
        }}
      />
      <div
        className='centrify'
        style={{
          ...containerStyles,
          display: !data.length || loading ? 'flex' : 'none',
        }}
      >
        {loading ? (
          <Loader />
        ) : (
          <Text size={14} className='text--grey-200'>
            No data
          </Text>
        )}
      </div>
    </>
  );
};
export default LiquidityChart;
